import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { useEffect, useState } from "react";
import { getTokenClaims } from "../../core/auth/auth";
import { type UserRead, usersApi } from "../../core/api/usersApi";

const PROVIDER_LABEL: Record<string, string> = {
  local: "Local",
  google: "Google",
  microsoft: "Microsoft",
};

const ROLE_LABEL: Record<string, string> = {
  admin: "Admin",
  user_manager: "Gestor de usuarios",
  shift: "Turnos",
  tax: "Fiscal",
  user: "Usuario",
};

function formatDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  return new Intl.DateTimeFormat("es-ES", {
    day: "2-digit",
    month: "long",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(iso));
}

// ---------------------------------------------------------------------------
// Section card wrapper
// ---------------------------------------------------------------------------

const Section = ({ title, icon, children }: { title: string; icon: React.ReactNode; children: React.ReactNode }) => (
  <Box
    sx={{
      bgcolor: "var(--color-surface)",
      border: "1px solid var(--color-border)",
      borderRadius: 2,
      p: 3,
    }}
  >
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2.5 }}>
      <Box sx={{ color: "var(--color-text-secondary)", display: "flex" }}>{icon}</Box>
      <Typography fontWeight={600} fontSize={15}>
        {title}
      </Typography>
    </Box>
    {children}
  </Box>
);

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

const Profile = () => {
  const claims = getTokenClaims();
  const [user, setUser] = useState<UserRead | null>(null);
  const [loading, setLoading] = useState(true);

  // Name edit
  const [editingName, setEditingName] = useState(false);
  const [nameValue, setNameValue] = useState("");
  const [nameError, setNameError] = useState("");
  const [nameSaving, setNameSaving] = useState(false);
  const [nameSuccess, setNameSuccess] = useState(false);

  // Password change
  const [pwCurrent, setPwCurrent] = useState("");
  const [pwNew, setPwNew] = useState("");
  const [pwConfirm, setPwConfirm] = useState("");
  const [pwError, setPwError] = useState("");
  const [pwSaving, setPwSaving] = useState(false);
  const [pwSuccess, setPwSuccess] = useState(false);

  useEffect(() => {
    if (!claims) return;
    usersApi
      .get(claims.sub)
      .then((u) => {
        setUser(u);
        setNameValue(u.name);
      })
      .finally(() => setLoading(false));
  }, []);

  const handleSaveName = async () => {
    if (nameValue.trim().length < 2) {
      setNameError("Mínimo 2 caracteres.");
      return;
    }
    setNameSaving(true);
    setNameError("");
    try {
      const updated = await usersApi.update(user!.id, { name: nameValue.trim() });
      setUser(updated);
      setNameValue(updated.name);
      setEditingName(false);
      setNameSuccess(true);
      setTimeout(() => setNameSuccess(false), 3000);
    } catch {
      setNameError("No se pudo guardar el nombre.");
    } finally {
      setNameSaving(false);
    }
  };

  const handleCancelName = () => {
    setEditingName(false);
    setNameValue(user?.name ?? "");
    setNameError("");
  };

  const validatePassword = (p: string) =>
    /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[\W_]).{8,}$/.test(p);

  const handleSavePassword = async () => {
    setPwError("");
    if (!pwCurrent) { setPwError("Introduce tu contraseña actual."); return; }
    if (!validatePassword(pwNew)) {
      setPwError("Mín. 8 caracteres, 1 mayúscula, 1 minúscula, 1 dígito y 1 especial.");
      return;
    }
    if (pwNew !== pwConfirm) { setPwError("Las contraseñas no coinciden."); return; }
    setPwSaving(true);
    try {
      await usersApi.update(user!.id, { password: pwNew });
      setPwCurrent("");
      setPwNew("");
      setPwConfirm("");
      setPwSuccess(true);
      setTimeout(() => setPwSuccess(false), 3000);
    } catch {
      setPwError("No se pudo actualizar la contraseña.");
    } finally {
      setPwSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", pt: 8 }}>
        <CircularProgress size={32} />
      </Box>
    );
  }

  if (!user) return null;

  const initial = user.name.charAt(0).toUpperCase();
  const isLocal = user.provider === "local";

  return (
    <Box sx={{ maxWidth: 680 }}>
      <Typography variant="h5" fontWeight={600} mb={3}>
        Mi perfil
      </Typography>

      {/* ── Identity card ─────────────────────────────────────── */}
      <Box
        sx={{
          bgcolor: "var(--color-surface)",
          border: "1px solid var(--color-border)",
          borderRadius: 2,
          p: 3,
          mb: 2,
          display: "flex",
          alignItems: "center",
          gap: 2.5,
        }}
      >
        <Avatar
          sx={{
            width: 64,
            height: 64,
            fontSize: 26,
            fontWeight: 700,
            bgcolor: "var(--color-accent)",
            flexShrink: 0,
          }}
        >
          {initial}
        </Avatar>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          {/* Name row */}
          {editingName ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <TextField
                size="small"
                value={nameValue}
                onChange={(e) => setNameValue(e.target.value)}
                error={!!nameError}
                helperText={nameError}
                autoFocus
                sx={{ flex: 1 }}
                onKeyDown={(e) => { if (e.key === "Enter") handleSaveName(); if (e.key === "Escape") handleCancelName(); }}
              />
              <Button
                size="small"
                variant="contained"
                disableElevation
                startIcon={nameSaving ? <CircularProgress size={14} color="inherit" /> : <SaveOutlinedIcon fontSize="small" />}
                onClick={handleSaveName}
                disabled={nameSaving}
              >
                Guardar
              </Button>
              <Button size="small" onClick={handleCancelName} disabled={nameSaving}>
                Cancelar
              </Button>
            </Box>
          ) : (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography fontWeight={600} fontSize={18} noWrap>
                {user.name}
              </Typography>
              <Tooltip title="Editar nombre">
                <IconButton size="small" onClick={() => setEditingName(true)} sx={{ color: "var(--color-text-secondary)" }}>
                  <EditOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              {nameSuccess && (
                <Typography variant="caption" color="success.main">
                  Guardado
                </Typography>
              )}
            </Box>
          )}

          <Typography variant="body2" color="text.secondary" noWrap>
            {user.email}
          </Typography>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.75 }}>
            <Chip
              label={PROVIDER_LABEL[user.provider] ?? user.provider}
              size="small"
              sx={{
                bgcolor: "rgba(15,118,110,0.10)",
                color: "var(--color-accent)",
                fontWeight: 600,
                border: "none",
              }}
            />
            {user.roles.map((r) => (
              <Chip
                key={r}
                label={ROLE_LABEL[r] ?? r}
                size="small"
                variant="outlined"
              />
            ))}
          </Box>
        </Box>
      </Box>

      {/* ── Activity ──────────────────────────────────────────── */}
      <Section
        title="Actividad"
        icon={
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <rect x="3" y="4" width="18" height="18" rx="2" />
            <line x1="16" y1="2" x2="16" y2="6" />
            <line x1="8" y1="2" x2="8" y2="6" />
            <line x1="3" y1="10" x2="21" y2="10" />
          </svg>
        }
      >
        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
          <Box>
            <Typography variant="caption" color="text.secondary" display="block" mb={0.25}>
              Miembro desde
            </Typography>
            <Typography variant="body2" fontWeight={500}>
              {formatDate(user.created_at)}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary" display="block" mb={0.25}>
              Último acceso
            </Typography>
            <Typography variant="body2" fontWeight={500}>
              {formatDate(user.last_login)}
            </Typography>
          </Box>
        </Box>
      </Section>

      {/* ── Password (local only) ──────────────────────────────── */}
      {isLocal && (
        <Box mt={2}>
          <Section title="Seguridad" icon={<LockOutlinedIcon fontSize="small" />}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, maxWidth: 360 }}>
              <TextField
                size="small"
                label="Contraseña actual"
                type="password"
                value={pwCurrent}
                onChange={(e) => setPwCurrent(e.target.value)}
                autoComplete="current-password"
              />
              <TextField
                size="small"
                label="Nueva contraseña"
                type="password"
                value={pwNew}
                onChange={(e) => setPwNew(e.target.value)}
                autoComplete="new-password"
              />
              <TextField
                size="small"
                label="Confirmar nueva contraseña"
                type="password"
                value={pwConfirm}
                onChange={(e) => setPwConfirm(e.target.value)}
                autoComplete="new-password"
              />

              {pwError && (
                <Typography variant="caption" color="error">
                  {pwError}
                </Typography>
              )}
              {pwSuccess && (
                <Typography variant="caption" color="success.main">
                  Contraseña actualizada correctamente.
                </Typography>
              )}

              <Box>
                <Button
                  variant="contained"
                  disableElevation
                  size="small"
                  onClick={handleSavePassword}
                  disabled={pwSaving}
                  startIcon={pwSaving ? <CircularProgress size={14} color="inherit" /> : undefined}
                >
                  Cambiar contraseña
                </Button>
              </Box>
            </Box>
          </Section>
        </Box>
      )}
    </Box>
  );
};

export default Profile;
